import { useState, useEffect } from 'react';
import { FormInput } from '../ui/FormInput';
import { validateIvorianPhone, formatIvorianPhone, isEmailUnique, isPhoneUnique } from '../../utils/validation';
import { db } from '../../lib/firebase';
import { hashPassword } from '../../utils/auth';

const DEPARTMENT_REPORTS = [
  { id: 'worshipReports', label: 'Gestion de Culte' },
  { id: 'adnReports', label: 'ADN - Amis des Nouveaux' },
  { id: 'financeReports', label: 'Finance' },
  { id: 'academieReports', label: 'Académie' },
  { id: 'sainteCeneReports', label: 'Sainte Cène' },
  { id: 'sonoReports', label: 'Communication & Sono' }
];

interface User {
  id: string;
  username: string;
  email: string;
  phone?: string;
  role: 'admin' | 'user' | 'responsable' | 'pasteur';
  assignedReports?: string[];
}

interface UserFormProps {
  onSubmit: (userData: any) => void;
  onCancel: () => void;
  initialData?: User | null;
}

export function UserForm({ onSubmit, onCancel, initialData }: UserFormProps) {
  const [formData, setFormData] = useState<{
    username: string;
    email: string;
    phone: string;
    role: 'admin' | 'user' | 'responsable' | 'pasteur';
    assignedReports: string[];
  }>({
    username: '',
    email: '',
    phone: '',
    role: 'user',
    assignedReports: []
  });
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        username: initialData.username,
        email: initialData.email,
        phone: initialData.phone || '',
        role: initialData.role,
        assignedReports: initialData.assignedReports || []
      });
      setSelectedDepartments(initialData.assignedReports || []);
    }
  }, [initialData]);

  const validateForm = async () => {
    const newErrors = {
      email: '',
      phone: ''
    };

    // Validation du téléphone
    if (formData.phone) {
      if (!validateIvorianPhone(formData.phone)) {
        newErrors.phone = 'Numéro de téléphone invalide';
      } else {
        const isUnique = await isPhoneUnique(formData.phone, db, initialData?.id);
        if (!isUnique) {
          newErrors.phone = 'Ce numéro est déjà utilisé';
        }
      }
    }

    // Validation de l'email
    const isUnique = await isEmailUnique(formData.email, db, initialData?.id);
    if (!isUnique) {
      newErrors.email = 'Cet email est déjà utilisé';
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.phone;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (await validateForm()) {
      const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
      
      const userData: any = {
        ...formData,
        phone: cleanPhone,
        email: formData.email.toLowerCase(),
        assignedReports: formData.role === 'responsable' ? selectedDepartments : []
      };

      // Hash password for new users
      if (!initialData) {
        const hashedPassword = await hashPassword('123456');
        userData.password = hashedPassword;
      }

      onSubmit(userData);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatIvorianPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value as 'admin' | 'user' | 'responsable' | 'pasteur';
    setFormData(prev => ({ ...prev, role: newRole }));
    
    // Clear selected departments if role is not 'responsable'
    if (newRole !== 'responsable') {
      setSelectedDepartments([]);
    }
  };

  const handleDepartmentChange = (departmentId: string, checked: boolean) => {
    if (checked) {
      setSelectedDepartments(prev => [...prev, departmentId]);
    } else {
      setSelectedDepartments(prev => prev.filter(id => id !== departmentId));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-xl font-semibold mb-6">
        {initialData ? 'Modifier l\'utilisateur' : 'Ajouter un utilisateur'}
      </h2>
      
      <FormInput
        label="Nom d'utilisateur"
        value={formData.username}
        onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
        required
      />
      
      <div>
        <FormInput
          type="email"
          label="Email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          required
        />
        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
      </div>
      
      <div>
        <FormInput
          type="tel"
          label="Numéro de téléphone"
          placeholder="07 XX XX XX XX"
          value={formData.phone}
          onChange={handlePhoneChange}
        />
        {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
        <p className="mt-1 text-sm text-gray-500">
          Format: 07 XX XX XX XX (10 chiffres)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Rôle
        </label>
        <select
          value={formData.role}
          onChange={handleRoleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          required
        >
          <option value="user">Utilisateur</option>
          <option value="responsable">Responsable</option>
          <option value="admin">Administrateur</option>
          <option value="pasteur">Pasteur</option>
        </select>
      </div>

      {/* Assignation des rapports de département (visible uniquement pour les responsables) */}
      {formData.role === 'responsable' && (
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-3">
            Rapports de département assignés
          </label>
          <div className="space-y-2 p-4 bg-gray-50 rounded-md border border-gray-200">
            {DEPARTMENT_REPORTS.map((report) => (
              <label key={report.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedDepartments.includes(report.id)}
                  onChange={(e) => handleDepartmentChange(report.id, e.target.checked)}
                  className="mr-3 h-4 w-4 text-[#00665C] focus:ring-[#00665C] border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">{report.label}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Sélectionnez les rapports de département auxquels ce responsable aura accès.
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
        >
          {initialData ? 'Modifier' : 'Ajouter'}
        </button>
      </div>
    </form>
  );
}